import router from 'next/router';
import fetchNext from '@features/api/fetchNext';
import getPublicEnv from '@utils/getPublicEnv';
import { IGoogleMapLocation } from '@features/checkout/typescript';

class NextProductApi {
  private endpoints = {
    getRelatedProducts: () => `product/relatedProducts`,
    getRecommendedProducts: (isDecorativeArticle = false) => `product/recommendedProducts/${isDecorativeArticle}`,
    getConfiguratorBuild: () => `product/configuratorBuild`,
    getProductConfig: () => `product/productConfig`,
    getObjectStorageData: () => `product/objectStorage/getObject`,
    getNearestStores: (
      productId: number,
      limit: number,
      pointsGroups: string[] = ['VOX_INTERIORS', 'VOXBOX_STUDIO', 'VOX_FURNITURES'],
    ) => `product/${productId}/nearestStores?limit=${limit}&pointsGroups=${pointsGroups.join(',')}`,
    getNearestStoresWithGeolocation: (
      productId: number,
      geolocation: IGoogleMapLocation,
      limit: number,
      pointsGroups: string[] = ['VOX_INTERIORS', 'VOXBOX_STUDIO', 'VOX_FURNITURES'],
      useMerchandisingDisplayStatuses = false,
    ) =>
      `product/${productId}/nearestStores?lat=${geolocation.lat}&lng=${geolocation.lng}&limit=${limit}&pointsGroups=${pointsGroups.join(',')}&useMerchandisingDisplayStatuses=${useMerchandisingDisplayStatuses}`,
    getRichContent: (productId: number) => `product/${productId}/richContent`,
    getBannersPromotionSections: () => `product/bannersPromotionSections`,
    getMessagesCartSections: () => `cart/messagesCartSections`,
    getProductSchema: (productId: number, schemaObjectUuid?: string) =>
      schemaObjectUuid
        ? `product/${productId}/schema?schemaObjectUuid=${schemaObjectUuid}`
        : `product/${productId}/schema`,
  };

  private static getLocale() {
    const isServerSide = typeof window === 'undefined';
    if (isServerSide) return process.env.DEFAULT_LOCALE;

    const { locale } = router;
    return locale || getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE');
  }

  public async getRelatedProducts(
    productID: string,
    offer: string,
    pricelists: string,
    storages: string,
    langCode: string,
    offset = 0,
    limit = 4,
  ) {
    const method = 'POST';

    const idCast = parseInt(productID, 10);
    const body = JSON.stringify({
      productID: idCast,
      offer,
      pricelists,
      storages,
      langCode,
      offset,
      limit,
    });

    const { json } = await fetchNext(this.endpoints.getRelatedProducts(), { method, body }, NextProductApi.getLocale());

    return json;
  }

  public async getRecommendedProducts(
    productID: string,
    offer: string,
    pricelists: string,
    storages: string,
    langCode: string,
    offset = 0,
    limit = 4,
    isDecorativeArticle = false,
  ) {
    const method = 'POST';

    const idCast = parseInt(productID, 10);
    const body = JSON.stringify({
      productID: idCast,
      offer,
      pricelists,
      storages,
      langCode,
      offset,
      limit,
    });

    const { json } = await fetchNext(
      this.endpoints.getRecommendedProducts(isDecorativeArticle),
      { method, body },
      NextProductApi.getLocale(),
    );

    return json;
  }

  public async getConfiguratorBuild() {
    const method = 'GET';

    const { json } = await fetchNext(this.endpoints.getConfiguratorBuild(), { method }, NextProductApi.getLocale());

    return json;
  }

  public async getProductConfig(
    productID: string,
    offer: string,
    pricelists: string,
    storages: string,
    langCode: string,
    publication: string,
  ) {
    const method = 'POST';

    const idCast = parseInt(productID, 10);
    const body = JSON.stringify({
      productID: idCast,
      offer,
      pricelists,
      storages,
      langCode,
      publication,
    });

    const { json } = await fetchNext(this.endpoints.getProductConfig(), { method, body }, NextProductApi.getLocale());

    return json;
  }

  public async getObjectStorageData(uuid: string) {
    const method = 'POST';

    const body = JSON.stringify({
      uuid,
    });

    const { json } = await fetchNext(
      this.endpoints.getObjectStorageData(),
      { method, body },
      NextProductApi.getLocale(),
    );

    return json;
  }

  public async getNearestStores(
    productId: number,
    geolocation: IGoogleMapLocation | null = null,
    limit = 3,
    useMerchandisingDisplayStatuses = false,
    pointsGroups: string[] = ['VOX_INTERIORS', 'VOXBOX_STUDIO', 'VOX_FURNITURES'],
  ) {
    const method = 'GET';

    const url =
      geolocation && geolocation?.lat && geolocation.lng
        ? this.endpoints.getNearestStoresWithGeolocation(
            productId,
            geolocation,
            limit,
            pointsGroups,
            useMerchandisingDisplayStatuses,
          )
        : this.endpoints.getNearestStores(productId, limit, pointsGroups);

    const { json } = await fetchNext(url, { method }, NextProductApi.getLocale());

    return json;
  }

  public async getRichContent(productId: number) {
    const method = 'GET';

    const { json } = await fetchNext(this.endpoints.getRichContent(productId), { method }, NextProductApi.getLocale());

    return json;
  }

  public async getBannersPromotionSections(
    offer: string,
    pricelists: string,
    storages: string,
    langCode: string,
    publication: string,
  ) {
    const method = 'POST';

    const body = JSON.stringify({
      offer,
      pricelists,
      storages,
      langCode,
      publication,
    });
    const { json } = await fetchNext(
      this.endpoints.getBannersPromotionSections(),
      { method, body },
      NextProductApi.getLocale(),
    );

    return json;
  }

  public async getMessagesCartSections(
    offer: string,
    pricelists: string,
    storages: string,
    langCode: string,
    publication: string,
  ) {
    const method = 'POST';

    const body = JSON.stringify({
      offer,
      pricelists,
      storages,
      langCode,
      publication,
    });
    const { json } = await fetchNext(
      this.endpoints.getMessagesCartSections(),
      { method, body },
      NextProductApi.getLocale(),
    );
    return json;
  }

  public async getProductSchema(productId: number, schemaObjectUuid?: string) {
    const method = 'GET';

    const { json } = await fetchNext(
      this.endpoints.getProductSchema(productId, schemaObjectUuid),
      { method },
      NextProductApi.getLocale(),
    );

    return json;
  }
}

export default new NextProductApi();
